// ============================================
// Lovense Manager — v0.2.4+ (Phase 2)
//
// Pairing: anonymous random UUID per install, persisted in localStorage.
// Commands: LAN-direct POST first (low latency), VPS cloud-relay fallback.
// Pattern player: sequential setTimeout chain (NEVER nested setInterval —
// see memory/web-modules.md). Patreon-gated on the caller side.
//
// Privacy: no pulse history is sent anywhere. Only pairing metadata (uid
// + LAN domain) lives on the VPS for 24h. uid is NOT linked to patreon
// account, IP, or save data — it's a throwaway UUID per install that
// the user can reset at any time via Disconnect.
// ============================================

import { events, Events } from '../engine/events.js';

const VPS_BASE = 'https://www.pinkedandowned.com/api/lovense';
const POLL_INTERVAL_MS = 2000;   // status polling while QR modal is open
const POLL_TIMEOUT_MS = 240000;  // 4 min: matches the QR validity window
const COMMAND_TIMEOUT_MS = 2000; // LAN-direct POST timeout before cloud fallback
                                 // (reduced from 5s in 0.2.3 — only used when
                                 // _isLocalNet === true, so LAN is known good;
                                 // fresh probe uses LAN_PROBE_TIMEOUT_MS)
const LAN_PROBE_TIMEOUT_MS = 1000; // one-shot LAN reachability probe on pair

const STORAGE_KEYS = {
    uid: 'PnO_LovenseUid',
    session: 'PnO_LovenseSession',       // { domain, httpsPort, httpPort, toys, platform }
    enabled: 'PnO_LovenseEnabled',       // user toggle in Settings
    intensity: 'PnO_LovenseIntensity',   // 0-100 master volume scalar
    autoArousal: 'PnO_LovenseAutoArousal',
    masterPulse: 'PnO_LovenseMasterPulse',
    ambient: 'PnO_LovenseAmbient',
    climax: 'PnO_LovenseClimax',
    vibrateOnly: 'PnO_LovenseVibrateOnly', // opt-out of multi-function (All:N) mode
    basePattern: 'PnO_LovenseBasePattern', // 'steady'|'wave'|'pulse'|'heartbeat'
};

/**
 * Generate a random UUID v4 without relying on crypto.randomUUID (Safari
 * iframe sandboxes sometimes block it). Good enough for an anonymous
 * throwaway identifier — not a security token.
 */
function makeUid() {
    const rnd = (n) => Math.floor(Math.random() * n);
    const hex = (len) => Array.from({ length: len }, () => rnd(16).toString(16)).join('');
    return `pno-${hex(8)}-${hex(4)}-${hex(12)}`;
}

// Base-pulse refresh timing. We send a command with timeSec=30 and refresh
// every 25s, so there's a 5s safety overlap before the command would expire.
// If the tab closes or the game crashes, the toy still stops within 30s on
// its own — no indefinite-run state.
const BASE_REFRESH_INTERVAL_MS = 25000;
const BASE_COMMAND_DURATION_SEC = 30;

// Selectable base-pattern shapes. Each is a normalized (0.0-1.0) strength
// array + step interval. At send time, each value is multiplied by the
// arousal-bin level (0-15), floor-clamped to 1 when non-zero so the toy
// never clicks fully off mid-wave, then passed through _scaleForVolume for
// the master-volume slider. Lovense Remote App loops the strength sequence
// for the full timeSec=30 window, so short cycles (3s) repeat ~9 times
// within one refresh window.
const BASE_PATTERNS = {
    // 'steady' is null — special case: single Function/All:N command (legacy)
    steady: null,

    // Sine wave: smooth rise and fall, 16 steps × 200ms = 3.2s per cycle
    wave: {
        steps: [0.55, 0.70, 0.85, 0.95, 1.0, 0.95, 0.85, 0.70,
                0.55, 0.40, 0.25, 0.15, 0.10, 0.15, 0.25, 0.40],
        intervalMs: 200,
    },

    // Pulse: rhythmic on-off with smooth tails, 8 steps × 300ms = 2.4s
    pulse: {
        steps: [1.0, 0.85, 0.5, 0.15, 0.0, 0.15, 0.5, 0.85],
        intervalMs: 300,
    },

    // Heartbeat: lub-dub + rest, 12 steps × 200ms = 2.4s
    heartbeat: {
        steps: [0.8, 1.0, 0.3, 0.1, 0.6, 1.0, 0.2, 0.0, 0.0, 0.0, 0.0, 0.1],
        intervalMs: 200,
    },
};

class LovenseManager {
    constructor() {
        this._uid = null;
        this._session = null;     // { domain, httpsPort, httpPort, toys, platform } after pairing
        this._pollAbort = false;  // flag to cancel in-flight status polling
        this._patternTimer = null;
        this._activeCommand = null;
        this._lastCommandAt = 0;

        // Base-pulse state (continuous background vibration tied to arousal)
        this._baseIntensity = 0;       // desired level (0 = off)
        this._baseRefreshTimer = null; // periodic refresh so the toy doesn't auto-stop
        this._spikeActive = false;     // true while a spike is playing
        this._spikeResumeTimer = null; // scheduled base resume after spike ends

        // LAN reachability cache. Tri-state:
        //   null  — unknown, will probe on next pair/reconnect
        //   true  — LAN-direct works, use it as primary transport
        //   false — LAN is blocked (Safari private-IP sandbox, different
        //           network, Remote App not reachable). Skip LAN on every
        //           command and go straight to cloud relay — saves the 5s
        //           AbortController timeout per command, which matters for
        //           pattern playback where each wasted timeout delays the
        //           user-perceived start of the sequence.
        // Probed once at pair time via _probeLan(), not persisted across
        // page loads (re-probed on tryReconnect). Flipped on positive /
        // negative command results as well so consistent failures migrate
        // to the cloud path within one command.
        this._isLocalNet = null;

        // Restore persisted uid + session (if any) without touching the network
        try {
            this._uid = localStorage.getItem(STORAGE_KEYS.uid);
            const raw = localStorage.getItem(STORAGE_KEYS.session);
            if (raw) this._session = JSON.parse(raw);
        } catch(e) {
            console.warn('[Lovense] restore failed:', e && e.message);
        }
    }

    // ── Public: state ─────────────────────────────────────

    isEnabled() {
        return localStorage.getItem(STORAGE_KEYS.enabled) === 'true';
    }

    isPaired() {
        // A session WITHOUT a LAN domain is still a valid pairing: some
        // Android Lovense Remote configs (no local-network permission /
        // LAN server off) send their pairing callback with an empty domain.
        // Commands then route through the VPS cloud relay, which only needs
        // the uid. Gating on `domain` here made the Settings panel show
        // "Not Paired" (and hid the test button, and muted every command)
        // even though the VPS session was alive with fresh heartbeats and
        // the pair toast had just fired. foundby:Hearts86 2026-07-03.
        // `domain` only selects the transport: _probeLan()/_sendLanDirect()
        // already fall back to cloud when it's missing.
        return !!this._session;
    }

    /**
     * Single source of truth for session writes. Every code path that
     * changes `_session` MUST go through here — never assign `this._session`
     * directly outside the constructor. Keeps localStorage and in-memory in
     * sync and clears transport cache when the session disappears.
     *
     * Recurring bug class (hit during test injection, potential in any
     * future code path): setting localStorage without updating _session
     * (or vice versa) causes isPaired() to disagree with what localStorage
     * says. This helper eliminates that by making the sync atomic.
     */
    _setSession(session) {
        this._session = session;
        try {
            if (session) {
                localStorage.setItem(STORAGE_KEYS.session, JSON.stringify(session));
            } else {
                localStorage.removeItem(STORAGE_KEYS.session);
                this._isLocalNet = null;  // forget cached transport when session cleared
            }
        } catch(e) { /* iframe / privacy mode, in-memory session still valid */ }
    }

    getToys() {
        return (this._session && this._session.toys) || {};
    }

    /** 0-100 master volume; scales every intensity before sending. */
    getIntensity() {
        const v = parseFloat(localStorage.getItem(STORAGE_KEYS.intensity));
        return Number.isFinite(v) ? Math.max(0, Math.min(100, v)) : 80;
    }

    setIntensity(pct) {
        const v = Math.max(0, Math.min(100, Math.round(pct)));
        localStorage.setItem(STORAGE_KEYS.intensity, String(v));
        // If a base loop is running, immediately re-scale + re-send so the
        // user feels the slider change without waiting up to 25s for the
        // next refresh tick. Skip if a spike is in flight (the spike will
        // resume at the new intensity naturally).
        if (this._baseIntensity > 0 && !this._spikeActive && this.isEnabled() && this.isPaired()) {
            this._startBaseLoop();
        }
    }

    getCategoryToggle(cat) {
        const key = STORAGE_KEYS[cat];
        if (!key) return false;
        const stored = localStorage.getItem(key);
        // All categories default ON now. `ambient` used to default OFF when
        // its semantic was "ambient vibration on deep trance"; since 0.2.4
        // it means "continuous base pulse that tracks arousal level" and
        // that IS the intended default experience — the toy shouldn't flip
        // on/off between story beats.
        if (stored === null) return true;
        return stored === 'true';
    }

    setCategoryToggle(cat, on) {
        const key = STORAGE_KEYS[cat];
        if (key) localStorage.setItem(key, on ? 'true' : 'false');
    }

    /**
     * Vibrate-only opt-out flag. DEFAULTS to false (= multi-function mode
     * via `All:N`). When true, falls back to legacy `Vibrate:N` behavior
     * which only drives toys with a vibration motor.
     *
     * Why default off: ~95% of Lovense players use a pure vibrator (Lush,
     * Hush, Domi, Ferri, Edge, Edge 2). For those, `All:N` and `Vibrate:N`
     * are functionally identical — Lovense Remote App routes both to the
     * single vibration channel. The remaining ~5% (Solace strokers,
     * Tenera suckers, Osci oscillators, Max pumps) are SILENT under
     * `Vibrate:N` because they have no vibration motor — `All:N` is the
     * only way to drive them without per-toy detection. So `All:N` is the
     * inclusive default; users on multi-function toys (Nora vibrate+rotate,
     * Flexer vibrate+fingering, Hyphy vibrate+thrusting) who find the
     * extra channels distracting can opt out via the Settings toggle.
     */
    getVibrateOnly() {
        return localStorage.getItem(STORAGE_KEYS.vibrateOnly) === 'true';
    }

    setVibrateOnly(on) {
        localStorage.setItem(STORAGE_KEYS.vibrateOnly, on ? 'true' : 'false');
    }

    /** Selected base-pattern name. Default 'wave'. */
    getBasePattern() {
        const v = localStorage.getItem(STORAGE_KEYS.basePattern);
        return (v && BASE_PATTERNS.hasOwnProperty(v)) ? v : 'wave';
    }

    setBasePattern(name) {
        if (!BASE_PATTERNS.hasOwnProperty(name)) return;
        localStorage.setItem(STORAGE_KEYS.basePattern, name);
        // Restart the loop immediately so the user feels the switch without
        // waiting for the next refresh tick (up to 25s away).
        if (this._baseIntensity > 0 && !this._spikeActive && this.isEnabled() && this.isPaired()) {
            this._startBaseLoop();
        }
    }

    /**
     * Action verb for Function commands — `Vibrate` if the user opted out
     * of multi-function mode, otherwise `All` (Lovense Remote App's built-in
     * fan-out: maps a single intensity onto every supported channel of the
     * connected toy, including Thrusting/Suction/Oscillate/Fingering/etc).
     */
    _actionVerb() {
        return this.getVibrateOnly() ? 'Vibrate' : 'All';
    }

    /**
     * Pattern feature string (the `F:...` field of the Pattern command's
     * `rule`) — `v` for vibrate-only, otherwise the full set of motion
     * channels: vibrate / rotate / pump / thrusting / fingering / suction
     * / depth / oscillate. The Remote App ignores any feature the toy
     * doesn't support, so the multi-feature string is safe on every toy.
     */
    _patternFeatures() {
        return this.getVibrateOnly() ? 'v' : 'v,r,p,t,f,s,d,o';
    }

    // ── Public: pairing ───────────────────────────────────

    /**
     * Request a fresh QR from our VPS. Lazily creates a uid on first call.
     * Returns { qr, code, uid } or throws.
     */
    async requestQr(displayName = 'PnOPlayer') {
        if (!this._uid) {
            this._uid = makeUid();
            localStorage.setItem(STORAGE_KEYS.uid, this._uid);
        }
        const resp = await fetch(`${VPS_BASE}/qr`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ uid: this._uid, uname: displayName }),
        });
        if (!resp.ok) {
            const detail = await resp.text().catch(() => '');
            throw new Error(`QR request failed (${resp.status}): ${detail.slice(0, 120)}`);
        }
        return await resp.json();
    }

    /**
     * Poll the VPS status endpoint until a toy shows up or timeout. Resolves
     * with the session object on success. Reject/abortable via cancelPolling().
     */
    async waitForPairing() {
        if (!this._uid) throw new Error('no uid — call requestQr first');
        this._pollAbort = false;
        const started = Date.now();
        while (!this._pollAbort && (Date.now() - started) < POLL_TIMEOUT_MS) {
            await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
            if (this._pollAbort) throw new Error('aborted');
            try {
                const resp = await fetch(`${VPS_BASE}/status/${encodeURIComponent(this._uid)}`);
                if (resp.status === 404) continue;  // not paired yet
                if (!resp.ok) continue;
                const data = await resp.json();
                const toys = data.toys || {};
                if (Object.keys(toys).length === 0) continue;  // paired but no toy

                // Reject leftover stale data from a previous session. If the
                // VPS `last_heartbeat` is older than the moment we started
                // polling, the row is from a previous pair run that hasn't
                // been pruned yet (24h TTL). Accepting it would flash
                // "Connected" for ~10s then flip to "Not Paired" as soon as
                // checkHealth sees the stale flag.
                //
                // We compute `heartbeatAt = now - last_heartbeat_age_sec*1000`
                // and require it to be within 3s of (or after) our poll
                // start — that 3s tolerates clock skew + request roundtrip
                // while still rejecting any multi-second-old leftover row.
                //
                // Fresh callbacks after QR scan reset `last_heartbeat` on
                // the VPS side, so once the user actually scans, the age
                // shrinks back to near-zero and this check passes naturally.
                const heartbeatAgeMs = (data.last_heartbeat_age_sec || 0) * 1000;
                const heartbeatAtMs = Date.now() - heartbeatAgeMs;
                if (heartbeatAtMs < started - 3000) {
                    // Stale pre-existing session — keep polling until Lovense
                    // sends a fresh callback from the new QR scan.
                    continue;
                }

                // Got it. Persist via _setSession (atomic in-memory + localStorage).
                this._setSession({
                    domain: data.domain,
                    httpsPort: data.https_port,
                    httpPort: data.http_port,
                    toys,
                    platform: data.platform,
                    pairedAt: Date.now(),
                });
                // Probe LAN reachability NOW so the very first story command
                // doesn't pay a 5s AbortController timeout on Safari. Awaited
                // so LOVENSE_PAIRED fires with a definitive transport known.
                await this._probeLan();
                events.emit(Events.LOVENSE_PAIRED, {
                    uid: this._uid, toys, domain: data.domain, platform: data.platform,
                });
                events.emit(Events.LOVENSE_TOY_LIST, { toys });
                return this._session;
            } catch(e) {
                // Transient network error; keep polling until timeout
            }
        }
        throw new Error(this._pollAbort ? 'aborted' : 'pairing timeout');
    }

    cancelPolling() {
        this._pollAbort = true;
    }

    async disconnect() {
        // 1. Kill all scheduled timers so nothing can fire a fresh command
        //    while we're in the middle of tearing down. This must happen
        //    BEFORE the Stop below, otherwise a base refresh tick or spike
        //    resume could re-vibrate the toy immediately after our Stop.
        this._stopPatternTimer();
        if (this._baseRefreshTimer) {
            clearTimeout(this._baseRefreshTimer);
            this._baseRefreshTimer = null;
        }
        if (this._spikeResumeTimer) {
            clearTimeout(this._spikeResumeTimer);
            this._spikeResumeTimer = null;
        }
        this._baseIntensity = 0;
        this._spikeActive = false;
        this.cancelPolling();

        // 2. AWAIT an explicit Stop before killing the VPS session.
        //    Without the await, the fetch for DELETE /session races ahead,
        //    the VPS relay verifies the uid still has a session row, and
        //    by the time the Stop command arrives at the cloud relay the
        //    session is already gone → 404 "not paired" → Stop silently
        //    dropped → toy keeps running until its current 30s command
        //    expires on its own. User-reported symptom: "der disconnect
        //    bemerkt das gerät noch nicht".
        if (this._session) {
            try {
                await this._sendCommand({
                    command: 'Function',
                    action: 'Stop',
                    apiVer: 1,
                });
            } catch(e) { /* best-effort — still tear down even if Stop fails */ }
        }

        // 3. Now it's safe to delete the VPS session. Any Stop command that
        //    was already accepted by Lovense Remote has already had effect.
        if (this._uid) {
            try {
                await fetch(`${VPS_BASE}/session/${encodeURIComponent(this._uid)}`, {
                    method: 'DELETE',
                });
            } catch(e) { /* best-effort */ }
        }

        // 4. Clear local state + notify listeners
        this._setSession(null);  // clears in-memory + localStorage + LAN cache
        // Keep the uid itself — same-install user might re-pair later.
        events.emit(Events.LOVENSE_DISCONNECTED, { reason: 'user' });
    }

    /**
     * Best-effort reconnect on game restart. Probes LAN reachability with
     * the cached session (short timeout) — if that succeeds, the session is
     * still valid AND we've refreshed `_isLocalNet` for downstream commands.
     * If LAN fails, falls back to a VPS status GET to verify the session
     * is at least still alive server-side (cloud-relay path will be used).
     * Silent on total failure — caller should check isPaired() after.
     */
    async tryReconnect() {
        if (!this._session || !this._uid) return false;
        // Probe LAN. _probeLan also refreshes _isLocalNet so the first
        // real command uses the right transport without a 5s discovery wait.
        if (await this._probeLan()) return true;
        try {
            const resp = await fetch(`${VPS_BASE}/status/${encodeURIComponent(this._uid)}`);
            if (!resp.ok) throw new Error('status ' + resp.status);
            const data = await resp.json();
            const toys = data.toys || {};
            if (Object.keys(toys).length === 0 || data.stale) throw new Error('stale or empty');
            this._setSession({
                domain: data.domain,
                httpsPort: data.https_port,
                httpPort: data.http_port,
                toys,
                platform: data.platform,
                pairedAt: Date.now(),
            });
            // Session refreshed from VPS — domain/port may have changed,
            // so re-probe LAN with the new values. Even if the probe fails
            // we still return true (session is alive on cloud), the flag
            // just tells downstream commands to skip the LAN attempt.
            await this._probeLan();
            events.emit(Events.LOVENSE_TOY_LIST, { toys });
            return true;
        } catch(e) {
            // Pairing expired. Drop the cached session so UI shows "Not Paired"
            this._setSession(null);
            events.emit(Events.LOVENSE_DISCONNECTED, { reason: 'expired' });
            return false;
        }
    }

    // ── Public: send commands ─────────────────────────────

    /**
     * Scale a raw intensity (0-20 Lovense scale) by the user's master-volume
     * slider. Hand-calibrated against Edge 2 felt output — purely linear
     * scaling made the slider feel dead at low positions (user report 2026-
     * 04-09: "der unterschied zwischen 100 und 30% ist nicht wahrnehmbar").
     *
     * ANCHORS is a lookup table: rows = raw intensity 0-20, columns = slider
     * anchor points at 30% / 50% / 100%. User provided target felt values
     * for the main base bins (4/6/9/12/15) at 30% and 50%; the other rows
     * are linearly interpolated between those anchors plus a reasonable
     * ceiling (col 100% = row index, i.e. at full volume we pass the raw
     * intensity straight through).
     *
     * For slider positions not exactly on an anchor we linearly interpolate
     * between the two adjacent columns (0→30, 30→50, 50→100) so the slider
     * feels smooth as the user drags it.
     *
     * Any non-zero raw intensity at a non-zero slider position is floored
     * at 1 — better to send a barely-there Vibrate:1 than send 0 (Stop).
     */
    _scaleForVolume(intensity) {
        if (intensity <= 0) return 0;
        const pct = Math.max(0, Math.min(100, this.getIntensity()));
        if (pct <= 0) return 0;

        // [at 30% slider, at 50% slider, at 100% slider]
        // Base bins marked ⚓ are user-calibrated; others interpolated.
        const ANCHORS = [
            [0,  0,  0 ],  // 0
            [1,  1,  1 ],  // 1
            [1,  1,  2 ],  // 2
            [1,  2,  3 ],  // 3
            [1,  2,  4 ],  // 4  ⚓ (Arousal 20-39 bin)
            [1,  3,  5 ],  // 5
            [2,  4,  6 ],  // 6  ⚓ (Arousal 40-59 bin)
            [2,  4,  7 ],  // 7
            [3,  4,  8 ],  // 8
            [4,  5,  9 ],  // 9  ⚓ (Arousal 60-79 bin)
            [4,  6,  10],  // 10
            [4,  6,  11],  // 11
            [5,  7,  12],  // 12 ⚓ (Arousal 80-99 bin)
            [5,  7,  13],  // 13
            [5,  8,  14],  // 14
            [6,  8,  15],  // 15 ⚓ (Arousal 100+ bin)
            [7,  9,  16],  // 16 — spike territory
            [7, 10,  17],  // 17
            [8, 11,  18],  // 18
            [9, 12,  19],  // 19
            [10, 13, 20],  // 20 — spike ceiling
        ];
        const idx = Math.max(0, Math.min(20, Math.round(intensity)));
        const [v30, v50, v100] = ANCHORS[idx];

        // Linearly interpolate across the three segments: 0→30, 30→50, 50→100
        let result;
        if (pct <= 30) {
            const t = pct / 30;
            result = Math.round(v30 * t);
        } else if (pct <= 50) {
            const t = (pct - 30) / 20;
            result = Math.round(v30 + (v50 - v30) * t);
        } else {
            const t = (pct - 50) / 50;
            result = Math.round(v50 + (v100 - v50) * t);
        }

        // Floor at 1 for any non-zero input — silence only on explicit zero
        if (result < 1) result = 1;
        if (result > 20) result = 20;
        return result;
    }

    /**
     * One-shot pulse: Vibrate at intensity (0-20 Lovense scale) for seconds.
     * Intensity is scaled by the master-volume setting.
     *
     * Does NOT coordinate with the base loop — if a base is running, this
     * command overrides it via stopPrevious:1 and the base will NOT auto-
     * resume. Used for manual test pulses + one-shot story events where the
     * caller wants full control. For base-aware spikes (auto-arousal trigger)
     * use spike() instead.
     */
    async pulse(intensity, seconds) {
        if (!this.isEnabled() || !this.isPaired()) return false;
        const scaled = this._scaleForVolume(intensity);
        return this._sendCommand({
            command: 'Function',
            action: `${this._actionVerb()}:${scaled}`,
            timeSec: Math.max(0, Math.min(60, seconds)),
            stopPrevious: 1,
            apiVer: 1,
        });
    }

    /**
     * Base-aware spike: overrides the base loop with a stronger pulse for
     * `seconds`, then restores the previous base level. Used by the arousal
     * auto-trigger so the continuous background buzz doesn't feel like it
     * drops back to zero between story beats.
     *
     * Intensity is scaled by master volume. Base level is untouched — it
     * resumes automatically after the spike finishes.
     */
    async spike(intensity, seconds) {
        if (!this.isEnabled() || !this.isPaired()) return false;
        const scaled = this._scaleForVolume(intensity);
        const dur = Math.max(0.5, Math.min(60, seconds));

        // Cancel any scheduled base refresh or resume — we're taking over
        if (this._baseRefreshTimer) {
            clearTimeout(this._baseRefreshTimer);
            this._baseRefreshTimer = null;
        }
        if (this._spikeResumeTimer) {
            clearTimeout(this._spikeResumeTimer);
            this._spikeResumeTimer = null;
        }
        this._spikeActive = true;

        const ok = await this._sendCommand({
            command: 'Function',
            action: `${this._actionVerb()}:${scaled}`,
            timeSec: dur,
            stopPrevious: 1,
            apiVer: 1,
        });

        // Schedule base resume shortly after the spike's natural end. The
        // +500ms buffer prevents the resume command from colliding with the
        // toy's own auto-stop at timeSec expiry.
        this._spikeResumeTimer = setTimeout(() => {
            this._spikeResumeTimer = null;
            this._spikeActive = false;
            if (this._baseIntensity > 0) {
                this._startBaseLoop();
            }
        }, Math.round(dur * 1000) + 500);

        return ok;
    }

    /**
     * Play a Lovense built-in preset for `seconds`. Names are the 4
     * the Remote App supports: `pulse`, `wave`, `fireworks`, `earthquake`.
     * Anything else is rejected with a warn.
     *
     * Payload shape extracted from the official basic-sdk core.min.js
     * (sendPresetCommand → sendRawCommand → POST /command). Same LAN
     * endpoint we already talk to, so this goes through `_sendCommand()`
     * unchanged and gets both LAN-direct + cloud-relay fallback for free.
     *
     * Preset intensity is NOT scalable — Lovense renders the shape server-
     * side inside the Remote App, so the master-volume slider has no effect.
     * Documented so the UI can decide whether to offer a warning.
     *
     * Coordinates with the base loop the same way `spike()` does: cancels
     * any pending refresh, marks a spike active, and restores the base
     * level after the preset's natural duration.
     */
    async preset(name, seconds = 5) {
        if (!this.isEnabled() || !this.isPaired()) return false;
        const ALLOWED = ['pulse', 'wave', 'fireworks', 'earthquake'];
        if (!ALLOWED.includes(name)) {
            console.warn('[Lovense] unknown preset:', name);
            return false;
        }
        const dur = Math.max(1, Math.min(60, Math.round(seconds)));

        // Cancel any scheduled base refresh or spike resume — we're taking over
        if (this._baseRefreshTimer) {
            clearTimeout(this._baseRefreshTimer);
            this._baseRefreshTimer = null;
        }
        if (this._spikeResumeTimer) {
            clearTimeout(this._spikeResumeTimer);
            this._spikeResumeTimer = null;
        }
        this._spikeActive = true;

        const ok = await this._sendCommand({
            command: 'Preset',
            apiVer: 1,
            name,
            timeSec: dur,
        });

        this._spikeResumeTimer = setTimeout(() => {
            this._spikeResumeTimer = null;
            this._spikeActive = false;
            if (this._baseIntensity > 0) {
                this._startBaseLoop();
            }
        }, dur * 1000 + 500);

        return ok;
    }

    /**
     * Play a custom Pattern sequence — Lovense renders it inside the
     * Remote App from a semicolon-separated strength list. Replaces the
     * client-side `playPattern()` setTimeout chain with a single HTTP
     * POST; the Remote App handles the step timing, so jittery Wi-Fi
     * doesn't stutter the pattern.
     *
     * Payload shape from basic-sdk core.min.js (sendPatternCommand):
     *   { command:'Pattern', apiVer:1,
     *     rule:'V:1;F:v;S:<intervalMs>#',
     *     strength:'a;b;c;...', timeSec:N, toy?:'...' }
     *
     * `rule.F` = feature letters (v=vibrate, r=rotate, p=pump, t=thrusting,
     * f=fingering, s=suction, d=depth, o=oscillate). We only emit `v`
     * because every Lovense-integrated story beat so far is vibrate-only;
     * extend the caller to pass a feature string when another device class
     * needs support.
     *
     * Arguments:
     *   strengthArray  Array of 0-20 ints. Max 50 entries (Lovense SDK
     *                  limit — anything longer is truncated).
     *   intervalMs     Per-step duration. Min 100, default 150.
     *   seconds        Total playback duration. 0/omitted → natural length
     *                  (array.length * interval). Values > natural loop
     *                  the pattern until `seconds` elapses.
     *
     * All strength values are scaled by the master-volume slider via
     * `_scaleForVolume()` so patterns feel consistent with pulses.
     */
    async pattern(strengthArray, intervalMs = 150, seconds = 0) {
        if (!this.isEnabled() || !this.isPaired()) return false;
        if (!Array.isArray(strengthArray) || strengthArray.length === 0) return false;
        // Enforce Lovense SDK limit — longer sequences are silently truncated
        const steps = strengthArray.length > 50 ? strengthArray.slice(0, 50) : strengthArray;
        const step = Math.max(100, Math.min(5000, Math.round(intervalMs)));

        // Scale every strength value by master-volume. Reuses the hand-
        // calibrated ANCHORS table so patterns honor the same "feels like"
        // curve as single pulses.
        const scaled = steps.map(v => this._scaleForVolume(v));
        const strengthStr = scaled.join(';');

        // Natural pattern length = steps * interval. If the caller didn't
        // specify a timeSec, use that so the toy plays the pattern exactly
        // once and stops on its own — matches the tab-close safety of the
        // normal Vibrate:N path. Clamp to 1-60s to match pulse()/spike().
        const natural = (scaled.length * step) / 1000;
        const dur = seconds > 0 ? seconds : natural;
        const durClamped = Math.max(1, Math.min(60, Math.round(dur)));

        // Base-loop coordination: same pattern as spike() + preset()
        if (this._baseRefreshTimer) {
            clearTimeout(this._baseRefreshTimer);
            this._baseRefreshTimer = null;
        }
        if (this._spikeResumeTimer) {
            clearTimeout(this._spikeResumeTimer);
            this._spikeResumeTimer = null;
        }
        this._spikeActive = true;

        const ok = await this._sendCommand({
            command: 'Pattern',
            apiVer: 1,
            rule: `V:1;F:${this._patternFeatures()};S:${step}#`,
            strength: strengthStr,
            timeSec: durClamped,
        });

        this._spikeResumeTimer = setTimeout(() => {
            this._spikeResumeTimer = null;
            this._spikeActive = false;
            if (this._baseIntensity > 0) {
                this._startBaseLoop();
            }
        }, durClamped * 1000 + 500);

        return ok;
    }

    /**
     * Set the sustained base-pulse level that runs between story beats.
     * 0 = off, 1-20 = Lovense Vibrate scale. Automatically scaled by the
     * master-volume setting. Idempotent — calling with the same level is a
     * no-op (avoids hammering the toy on every passive +1 arousal gain).
     *
     * The base is NOT sent while a spike is in flight — the spike's resume
     * timer will restart the base loop once the spike finishes.
     */
    setBase(intensity) {
        const target = Math.max(0, Math.min(20, Math.round(intensity)));
        if (target === this._baseIntensity) return;
        this._baseIntensity = target;

        // Dropping to 0 mid-spike MUST interrupt the spike, not just update
        // the desired level. Without this, the spike's Vibrate:N command
        // keeps running on the Remote App until its timeSec expires (up to
        // 6 seconds). User report 2026-04-09: "auf 0 arousal ist das
        // gerät einfach auf max" — classic orgasm flow where Ink bumps
        // Arousal to 100 (triggering a +20 delta spike) and then immediately
        // sets Arousal=0 on relief. The spike's Vibrate:20 was surviving the
        // drop to 0 by up to 6s because the old branch below just recorded
        // `_baseIntensity=0` and returned. Cancel the resume timer + send an
        // explicit Stop so the toy goes silent the instant arousal hits 0.
        if (target === 0 && this._spikeActive) {
            if (this._spikeResumeTimer) {
                clearTimeout(this._spikeResumeTimer);
                this._spikeResumeTimer = null;
            }
            this._spikeActive = false;
            // _stopBaseInternal's Stop gate is `!_spikeActive` — clear first,
            // then call so the explicit Stop command actually goes out.
            this._stopBaseInternal(/* sendStop = */ true);
            return;
        }

        if (this._spikeActive) {
            // Spike is running — base will resume via _spikeResumeTimer.
            // Just update the desired level so the resume picks it up.
            return;
        }
        if (target === 0) {
            this._stopBaseInternal(/* sendStop = */ true);
        } else {
            this._startBaseLoop();
        }
    }

    /** Read current base intensity (for UI + debugging). */
    getBase() {
        return this._baseIntensity;
    }

    /**
     * Poll the VPS for our session's current health. Returns one of:
     *   { ok: true,  paired: true,  stale: bool, toys, domain }   — session alive
     *   { ok: true,  paired: false }                                — session missing / 404
     *   { ok: false, error: string }                                — network / parse error
     *
     * If the session is gone (404) OR stale (last heartbeat > 90s),
     * this also clears the local cached session and fires
     * LOVENSE_DISCONNECTED so the Settings UI can update on its next
     * refresh. The caller (usually a periodic poller in the settings
     * UI) does NOT need to do the cleanup itself.
     */
    async checkHealth() {
        if (!this._uid) return { ok: true, paired: false };
        let resp;
        try {
            resp = await fetch(`${VPS_BASE}/status/${encodeURIComponent(this._uid)}`);
        } catch(e) {
            return { ok: false, error: e && e.message };
        }
        if (resp.status === 404) {
            // Session expired on the VPS — clean up local cache and notify
            if (this._session) {
                this._setSession(null);
                this._baseIntensity = 0;
                if (this._baseRefreshTimer) {
                    clearTimeout(this._baseRefreshTimer);
                    this._baseRefreshTimer = null;
                }
                events.emit(Events.LOVENSE_DISCONNECTED, { reason: 'stale' });
            }
            return { ok: true, paired: false };
        }
        if (!resp.ok) return { ok: false, error: 'status ' + resp.status };
        let data;
        try { data = await resp.json(); }
        catch(e) { return { ok: false, error: 'parse ' + (e && e.message) }; }

        const toys = data.toys || {};
        const isStale = !!data.stale;
        const hasToys = Object.keys(toys).length > 0;

        if (isStale || !hasToys) {
            if (this._session) {
                this._setSession(null);
                this._baseIntensity = 0;
                if (this._baseRefreshTimer) {
                    clearTimeout(this._baseRefreshTimer);
                    this._baseRefreshTimer = null;
                }
                events.emit(Events.LOVENSE_DISCONNECTED, { reason: 'stale' });
            }
            return { ok: true, paired: false, stale: isStale };
        }

        // Healthy — refresh our cached session if the domain/ports changed.
        // _setSession handles localStorage sync; we only need to manually
        // invalidate the LAN cache when the endpoint actually moved.
        const prev = this._session || {};
        const changed = prev.domain !== data.domain
            || prev.httpsPort !== data.https_port
            || prev.httpPort !== data.http_port;
        this._setSession({
            domain: data.domain,
            httpsPort: data.https_port,
            httpPort: data.http_port,
            toys,
            platform: data.platform,
            pairedAt: this._session?.pairedAt || Date.now(),
        });
        if (changed) this._isLocalNet = null;
        return {
            ok: true, paired: true, stale: false,
            toys, domain: data.domain,
        };
    }

    /** Public stop for the sustained base loop (without touching spikes). */
    stopBase() {
        this._baseIntensity = 0;
        this._stopBaseInternal(/* sendStop = */ true);
    }

    /**
     * Immediate full stop. Used for relief/caught/scene-end.
     * Kills patterns, base loop, pending spike resume, and sends Stop to toy.
     */
    async stop() {
        this._stopPatternTimer();
        this._stopBaseInternal(/* sendStop = */ false);
        this._baseIntensity = 0;
        if (this._spikeResumeTimer) {
            clearTimeout(this._spikeResumeTimer);
            this._spikeResumeTimer = null;
        }
        this._spikeActive = false;
        if (!this.isEnabled() || !this.isPaired()) return false;
        return this._sendCommand({
            command: 'Function',
            action: 'Stop',
            apiVer: 1,
        });
    }

    /**
     * Aggressive stop for cleanup on page load / save transition. Unlike
     * `stop()`, this ignores the `isEnabled()` gate and fires a Stop as
     * long as a session exists — the rationale being: if the previous
     * tab left the toy buzzing (e.g. a Vibrate command with timeSec:30
     * that still has 20s to go, or a Preset wave mid-render), we want to
     * silence it on the NEW tab's first frame even if the user has since
     * toggled the feature off. A Stop command is always a safe no-op when
     * nothing is running, so there's no downside to sending it.
     *
     * User report 2026-04-09: "direkt nach loading/start arousal=0 aber
     * gerät auf max" — classic leftover-from-previous-session case. The
     * normal `reset()` → `stopBase()` path bailed early on `!isEnabled()`
     * for users who had disabled the integration mid-session. forceStop
     * bypasses that guard so the toy always goes silent on load.
     */
    async forceStop() {
        this._stopPatternTimer();
        if (this._baseRefreshTimer) {
            clearTimeout(this._baseRefreshTimer);
            this._baseRefreshTimer = null;
        }
        if (this._spikeResumeTimer) {
            clearTimeout(this._spikeResumeTimer);
            this._spikeResumeTimer = null;
        }
        this._baseIntensity = 0;
        this._spikeActive = false;
        if (!this.isPaired()) return false;
        try {
            return await this._sendCommand({
                command: 'Function',
                action: 'Stop',
                apiVer: 1,
            });
        } catch(e) {
            return false;
        }
    }

    // ── Internal: base loop machinery ─────────────────────

    _startBaseLoop() {
        // Cancel any existing refresh timer
        if (this._baseRefreshTimer) {
            clearTimeout(this._baseRefreshTimer);
            this._baseRefreshTimer = null;
        }
        // Send initial command + schedule refresh
        const tick = () => {
            if (this._spikeActive) return;  // spike resume will restart us
            if (this._baseIntensity === 0) return;
            if (!this.isEnabled() || !this.isPaired()) return;

            // Slider at 0 → explicit Stop, no refresh scheduled
            if (this.getIntensity() <= 0) {
                this._sendCommand({ command: 'Function', action: 'Stop', apiVer: 1 });
                return;
            }

            const shape = BASE_PATTERNS[this.getBasePattern()];

            if (!shape) {
                // ── Steady mode: single Function/All:N (legacy behavior) ──
                const scaledBase = this._scaleForVolume(this._baseIntensity);
                this._sendCommand({
                    command: 'Function',
                    action: `${this._actionVerb()}:${scaledBase}`,
                    timeSec: BASE_COMMAND_DURATION_SEC,
                    stopPrevious: 1,
                    apiVer: 1,
                });
            } else {
                // ── Pattern mode: looping strength sequence ──
                // Scale each normalized step by the arousal-bin level, then
                // through the master-volume curve. Floor at 1 when base > 0
                // so the toy never clicks fully off mid-cycle.
                const rawSteps = shape.steps.map(v => {
                    const raw = Math.round(v * this._baseIntensity);
                    return this._baseIntensity > 0 ? Math.max(1, raw) : 0;
                });
                const scaledSteps = rawSteps.map(v => this._scaleForVolume(v));
                this._sendCommand({
                    command: 'Pattern',
                    apiVer: 1,
                    rule: `V:1;F:${this._patternFeatures()};S:${shape.intervalMs}#`,
                    strength: scaledSteps.join(';'),
                    timeSec: BASE_COMMAND_DURATION_SEC,
                    stopPrevious: 1,
                });
            }

            this._baseRefreshTimer = setTimeout(tick, BASE_REFRESH_INTERVAL_MS);
        };
        tick();
    }

    _stopBaseInternal(sendStop) {
        if (this._baseRefreshTimer) {
            clearTimeout(this._baseRefreshTimer);
            this._baseRefreshTimer = null;
        }
        if (sendStop && !this._spikeActive && this.isEnabled() && this.isPaired()) {
            // Only send an explicit Stop if no spike is active — otherwise the
            // spike's own auto-stop handles it.
            this._sendCommand({
                command: 'Function',
                action: 'Stop',
                apiVer: 1,
            });
        }
    }

    /**
     * Play a multi-step pattern via sequential setTimeout (NOT setInterval).
     * steps: Array<[intensity, seconds]>
     * If a new pattern starts, the previous one is cancelled first.
     */
    playPattern(steps, { loop = false } = {}) {
        if (!this.isEnabled() || !this.isPaired()) return false;
        if (!Array.isArray(steps) || steps.length === 0) return false;
        this._stopPatternTimer();

        let i = 0;
        const playNext = () => {
            if (i >= steps.length) {
                if (loop) i = 0;
                else return;
            }
            const [intensity, seconds] = steps[i++];
            this.pulse(intensity, seconds);
            this._patternTimer = setTimeout(playNext, seconds * 1000);
        };
        playNext();
        return true;
    }

    // ── Internal: send with LAN-first + cloud fallback ────

    async _sendCommand(payload) {
        this._lastCommandAt = Date.now();
        this._activeCommand = payload;

        // Try LAN-direct ONLY if we have a cached positive signal or if we
        // haven't probed yet (null). If _isLocalNet === false we skip LAN
        // entirely and go straight to cloud — saves the 2s AbortController
        // tax per command on Safari where LAN is known blocked. Flipping
        // the flag back to true requires a fresh probe via _probeLan()
        // (on pair / reconnect) — we don't re-attempt LAN on every command.
        if (this._isLocalNet !== false) {
            try {
                await this._sendLanDirect(payload);
                this._isLocalNet = true;  // positive confirmation
                events.emit(Events.LOVENSE_COMMAND_SENT, {
                    command: payload.command, action: payload.action,
                    timeSec: payload.timeSec, route: 'lan',
                });
                return true;
            } catch(e) {
                // LAN failed — flip the flag so subsequent commands skip
                // straight to cloud without eating another timeout.
                this._isLocalNet = false;
            }
        }

        // Cloud relay fallback
        try {
            await this._sendCloudRelay(payload);
            events.emit(Events.LOVENSE_COMMAND_SENT, {
                command: payload.command, action: payload.action,
                timeSec: payload.timeSec, route: 'cloud',
            });
            return true;
        } catch(e) {
            console.warn('[Lovense] both LAN + cloud failed:', e && e.message);
            return false;
        }
    }

    async _sendLanDirect(payload) {
        if (!this._session || !this._session.domain || !this._session.httpsPort) {
            throw new Error('no session');
        }
        const url = `https://${this._session.domain}:${this._session.httpsPort}/command`;
        const ctrl = new AbortController();
        const timer = setTimeout(() => ctrl.abort(), COMMAND_TIMEOUT_MS);
        try {
            // Use no-cors since the Lovense Remote app's LAN server does not
            // send CORS headers. The response is opaque but the POST fires.
            // On failure (network unreachable), fetch rejects — caught by caller.
            await fetch(url, {
                method: 'POST',
                mode: 'no-cors',
                headers: { 'Content-Type': 'text/plain' }, // avoids preflight
                body: JSON.stringify(payload),
                signal: ctrl.signal,
            });
        } finally {
            clearTimeout(timer);
        }
    }

    /**
     * One-shot LAN reachability probe. Fires a harmless `Function/Stop`
     * at the session's LAN endpoint with a short timeout (1s vs the 2s
     * used for real commands). Sets `_isLocalNet` to true on success,
     * false on failure. Called on pair + reconnect so the first real
     * command already knows the right transport.
     *
     * Shortcut: Lovense Connect (PC desktop app) reports a domain like
     * `127-0-0-1.lovense.club` in the pairing callback — that's localhost
     * via a wildcard DNS trick and is never blocked by Safari's private-
     * IP sandbox. Skip the probe and assume true in that case to avoid
     * the 1s wait on pair.
     *
     * Returns the boolean reachability result.
     */
    async _probeLan() {
        if (!this._session || !this._session.domain || !this._session.httpsPort) {
            this._isLocalNet = false;
            return false;
        }
        // Lovense Connect localhost shortcut — no probe needed
        if (this._session.domain.startsWith('127-') || this._session.domain === 'localhost') {
            this._isLocalNet = true;
            return true;
        }
        const url = `https://${this._session.domain}:${this._session.httpsPort}/command`;
        const ctrl = new AbortController();
        const timer = setTimeout(() => ctrl.abort(), LAN_PROBE_TIMEOUT_MS);
        try {
            await fetch(url, {
                method: 'POST',
                mode: 'no-cors',
                headers: { 'Content-Type': 'text/plain' },
                body: JSON.stringify({ command: 'Function', action: 'Stop', apiVer: 1 }),
                signal: ctrl.signal,
            });
            this._isLocalNet = true;
            return true;
        } catch(e) {
            this._isLocalNet = false;
            return false;
        } finally {
            clearTimeout(timer);
        }
    }

    async _sendCloudRelay(payload) {
        const body = { ...payload, uid: this._uid };
        const resp = await fetch(`${VPS_BASE}/command`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
        if (!resp.ok) {
            const detail = await resp.text().catch(() => '');
            throw new Error(`cloud relay ${resp.status}: ${detail.slice(0, 120)}`);
        }
    }

    _stopPatternTimer() {
        if (this._patternTimer) {
            clearTimeout(this._patternTimer);
            this._patternTimer = null;
        }
    }
}

// Singleton, imported by settings UI + story system
export const lovenseManager = new LovenseManager();

// Expose for test harness + debugging
if (typeof window !== 'undefined') {
    window._lovense = lovenseManager;
}
